import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { billingOpsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash, CreditCard, FloppyDisk } from '@phosphor-icons/react';

export default function PaymentModesPage() {
  const [modes, setModes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await billingOpsAPI.listAllPaymentModes(); setModes(data.payment_modes || []); }
    catch (e) { toast.error('Failed to load payment modes'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newLabel.trim()) { toast.error('Enter a payment mode name'); return; }
    setSaving(true);
    try {
      await billingOpsAPI.createPaymentMode({ label: newLabel.trim() });
      toast.success('Payment mode added');
      setNewLabel('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
    finally { setSaving(false); }
  };

  const toggleEnabled = async (m) => {
    try { await billingOpsAPI.updatePaymentMode(m.id, { enabled: !m.enabled }); load(); }
    catch (e) { toast.error('Failed to update'); }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Delete payment mode "${m.label}"?`)) return;
    try { await billingOpsAPI.deletePaymentMode(m.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div className="p-6" data-testid="payment-modes-page">
      <PageHeader title="Payment Modes" subtitle="Manage the payment methods available across invoices, sales orders and payments" />

      <div className="mt-6 max-w-2xl">
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex items-end gap-3 mb-4">
          <div className="flex-1">
            <label className="text-xs text-[#64748B] block mb-1">New Payment Mode</label>
            <Input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="e.g. Paytm, NEFT, Wallet" data-testid="new-payment-mode-input" />
          </div>
          <Button onClick={handleAdd} disabled={saving} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="add-payment-mode-btn">
            <Plus size={16} className="mr-1" /> Add
          </Button>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="payment-modes-table">
            <thead>
              <tr className="bg-[#F8FAFC] text-[#64748B] text-xs">
                <th className="text-left p-3">Payment Mode</th>
                <th className="text-left p-3 w-28">Code</th>
                <th className="text-center p-3 w-28">Enabled</th>
                <th className="p-3 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="text-center py-8 text-[#64748B]">Loading...</td></tr>
              ) : modes.map(m => (
                <tr key={m.id} className="border-t border-[#E2E8F0]" data-testid={`payment-mode-row-${m.code}`}>
                  <td className="p-3 font-medium flex items-center gap-2"><CreditCard size={16} className="text-[#64748B]" /> {m.label}</td>
                  <td className="p-3 text-xs text-[#64748B] font-mono">{m.code}</td>
                  <td className="p-3 text-center">
                    <Switch checked={m.enabled} onCheckedChange={() => toggleEnabled(m)} data-testid={`toggle-${m.code}`} />
                  </td>
                  <td className="p-3 text-right">
                    {!m.is_default && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDelete(m)} data-testid={`delete-${m.code}`}>
                        <Trash size={14} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#9CA3AF] mt-3">Default modes can't be deleted — disable them instead. Disabled modes won't appear in payment dropdowns.</p>
      </div>
    </div>
  );
}
